import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import Layout from '../components/Layout';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const PORTAAL_LABEL: Record<string, string> = {
  leden: 'Leden', collectief: 'Collectiviteit', allianz: 'Allianz Trade',
};

export const AdminToegangsverzoekenPage: React.FC = () => {
  const navigate = useNavigate();
  const { accessToken, isAuthenticated } = useAuthStore();
  const headers = { Authorization: `Bearer ${accessToken}` };

  const [rows, setRows] = useState<any[]>([]);
  const [filter, setFilter] = useState<'nieuw' | 'alle'>('nieuw');
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/admin/toegangsverzoeken?status=${filter}`, { headers })
        .then((x) => (x.ok ? x.json() : []));
      setRows(r);
    } catch { setError('Laden mislukt'); } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, filter]);

  useEffect(() => {
    if (!isAuthenticated || !accessToken) { navigate('/login'); return; }
    load();
  }, [isAuthenticated, accessToken, navigate, load]);

  const flash = (m: string) => { setToast(m); setError(''); setTimeout(() => setToast(''), 3500); };
  const afhandelen = async (id: string, status: string) => {
    setBusy(id);
    try {
      const r = await fetch(`${API_BASE_URL}/api/admin/toegangsverzoeken/${id}/afhandelen`, {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Mislukt'); }
      flash(status === 'afgewezen' ? 'Verzoek afgewezen.' : 'Verzoek gemarkeerd als afgehandeld.');
      load();
    } catch (e) { setError(e instanceof Error ? e.message : 'Mislukt'); } finally { setBusy(''); }
  };

  return (
    <Layout>
      <div className="mb-6">
        <p className="text-sm text-slate-500">
          Aanvragen voor toegang die binnenkomen via de "Toegang aanvragen"-knop op de portal-inlogschermen.
          Neem contact op met de aanvrager en nodig ze uit via het betreffende collectief; markeer het verzoek daarna als afgehandeld.
        </p>
      </div>
      {toast && <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800">{toast}</div>}
      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}

      <div className="flex items-baseline justify-between mb-3">
        <h2 className="font-display text-lg font-semibold text-mfb-blue">Toegangsverzoeken</h2>
        <div className="flex items-center gap-2">
          <button onClick={() => setFilter('nieuw')}
            className={`px-3 py-1.5 text-sm rounded-lg ${filter === 'nieuw' ? 'bg-mfb-blue text-white' : 'text-slate-600 border border-slate-300 hover:bg-slate-50'}`}>
            Nieuw
          </button>
          <button onClick={() => setFilter('alle')}
            className={`px-3 py-1.5 text-sm rounded-lg ${filter === 'alle' ? 'bg-mfb-blue text-white' : 'text-slate-600 border border-slate-300 hover:bg-slate-50'}`}>
            Alle
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500">Laden...</div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-500">
          Geen {filter === 'nieuw' ? 'openstaande ' : ''}toegangsverzoeken. Nieuwe aanvragen verschijnen hier automatisch.
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {rows.map((v) => (
            <div key={v.id} className="bg-white rounded-2xl border border-slate-200 p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-display text-lg font-semibold text-mfb-blue truncate">{v.naam || v.email}</h3>
                  <p className="text-xs text-slate-400">Aangevraagd {v.aangevraagd} · {PORTAAL_LABEL[v.portaal] || v.portaal}-portal</p>
                </div>
                <span className={`px-2.5 py-1 rounded-full text-xs font-medium shrink-0 ${
                  v.status === 'nieuw' ? 'bg-amber-100 text-amber-700'
                  : v.status === 'afgewezen' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'
                }`}>
                  {v.status === 'nieuw' ? 'Nieuw' : v.status === 'afgewezen' ? 'Afgewezen' : 'Afgehandeld'}
                </span>
              </div>

              <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <Row k="E-mail" v={v.email} />
                <Row k="Organisatie" v={v.organisatie || '—'} />
                {v.bericht && <div className="col-span-2"><Row k="Toelichting" v={v.bericht} /></div>}
              </dl>

              {v.status === 'nieuw' && (
                <div className="mt-4 flex gap-2">
                  <button disabled={busy === v.id} onClick={() => afhandelen(v.id, 'afgehandeld')}
                    className="px-4 py-2 text-sm font-semibold text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg disabled:opacity-50">
                    Markeer als afgehandeld
                  </button>
                  <button disabled={busy === v.id} onClick={() => { if (window.confirm('Verzoek afwijzen?')) afhandelen(v.id, 'afgewezen'); }}
                    className="px-4 py-2 text-sm font-medium text-red-700 border border-red-300 rounded-lg hover:bg-red-50 disabled:opacity-50">
                    Afwijzen
                  </button>
                  <a href={`mailto:${v.email}`}
                    className="px-4 py-2 text-sm font-medium text-mfb-blue border border-slate-300 rounded-lg hover:bg-slate-50">
                    Mail aanvrager
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
};

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div>
      <dt className="text-slate-400 text-xs uppercase tracking-wide">{k}</dt>
      <dd className="text-slate-700 break-words">{v}</dd>
    </div>
  );
}

export default AdminToegangsverzoekenPage;
